'use client';

import {
  type ClientApplicationConfigOptions,
  type ClientApplicationContext,
  getClientApplicationConfig,
  getClientApplicationContext,
} from '@hc-frontend/core-context';
import { createDomEventBus } from '@hc-frontend/core-events';
import { fetchJSON } from '@hc-frontend/core-utils-net';
import { getClientLoggerFactory } from '@hc-frontend/features-logger';
import {
  createDatadogPublisherCount,
  trackCountMetricFactory,
} from '@hc-frontend/features-performance-monitoring';
import {
  CompleteTrackingProvider,
  getSessionIdClientSide,
  getUserIdClientSide,
  useCompleteTrackingContext,
} from '@hc-frontend/features-tracking';
import type React from 'react';
import { useEffect } from 'react';

const APPLICATION_CONTEXT_WITHOUT_SESSION_CALLER =
  'ApplicationContextWithoutSession';

function runInitialization(
  tracker: ReturnType<typeof useCompleteTrackingContext>['tracker'],
) {
  if (typeof globalThis.initializeClientApplicationContext === 'function')
    return;

  const log = getClientLoggerFactory();

  const context: ClientApplicationContext = {
    log,
    getDomainUserId: getUserIdClientSide(tracker),
    getDomainSessionId: getSessionIdClientSide(tracker),
    eventBus: createDomEventBus(),
    trackCountMetric: trackCountMetricFactory({
      publish: createDatadogPublisherCount(log),
      log,
    }),
  };

  globalThis.initializeClientApplicationContext = () => context;
}

export function ApplicationContextWithoutSession({
  children,
}: React.PropsWithChildren) {
  const { tracker } = useCompleteTrackingContext();

  runInitialization(tracker);

  useEffect(() => {
    // Dump the user id and member id to the cookies
    const context = getClientApplicationContext(
      APPLICATION_CONTEXT_WITHOUT_SESSION_CALLER,
    );
    context.getDomainUserId();
    context.getDomainSessionId();

    const config = getClientApplicationConfig();

    fetchJSON(`${config.core.appBaseUrl}/api/external/domain/`, {
      credentials: 'include',
    }).catch((error) => {
      context.log({
        type: 'error',
        message: 'Failed to update domain identifiers',
        context: 'client',
        data: {
          rootCause: error,
        },
      });
    });
  }, []);

  return children;
}

export function ClientApplicationContext({
  children,
  config,
}: React.PropsWithChildren<{
  config: ClientApplicationConfigOptions;
}>) {
  globalThis.initializeClientApplicationConfig = () => config;

  return (
    <CompleteTrackingProvider
      adapter={{
        type: 'snowplow',
        props: {
          isProd: config.core.appEnvironment === 'prd',
        },
      }}
    >
      <ApplicationContextWithoutSession>
        {children}
      </ApplicationContextWithoutSession>
    </CompleteTrackingProvider>
  );
}
